import React, { useState } from 'react'
import { googlePayment } from 'braintree-web'
import { useBraintree } from './use-braintree';
import { useBasketStore, useGlobalStore, useOrderStore } from '../store/hooks/useStore';
import { GooglePayArgs, PaymentMethodSetupData } from '../types/payments';
import { getMobileAppOS } from '../utils/utils';
import { CARD_TYPES, PAYMENT_METHOD_IDS } from '../utils/constants';

export function useBraintreeGooglePay() {
  const { createBraintreeClientInstance } = useBraintree()
  const { shippingAddress } = useBasketStore()
  const {googlePayMerchantId, googlePayMerchantName, currency} = useGlobalStore()
  const [hasGooglePayButtonRendered, setHasGooglePayButtonRendered] = useState<boolean>(false)
  const orderStore = useOrderStore()

  const initGooglePayScript = () => {
    var elem = document.getElementById('googlePayScript')
    if (!elem && !getMobileAppOS()) {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.id = 'googlePayScript'
      script.src = 'https://pay.google.com/gp/p/js/pay.js'
      document.body.appendChild(script) 
    }
  }

  const initGooglePayButtons = async (data: PaymentMethodSetupData) => {
    if (shippingAddress && data.billingAddress) {
      await initGooglePayScript()
      await createBraintreeGooglePay(data)
    }
  }

  const createBraintreeGooglePayInstance = async () => {
    const { btClientInstance } = await createBraintreeClientInstance()
    const googlePayPaymentsClient = new google.payments.api.PaymentsClient({
      environment: `${process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'TEST'}`,
      merchantInfo: {
        merchantId: googlePayMerchantId ?? undefined,
        merchantName: googlePayMerchantName ?? undefined
      }
    });

    const googlePayInstance = googlePayment.create({
      client: btClientInstance, // From braintree.client.create, see below for full example
      googlePayVersion: 2,
      googleMerchantId: googlePayMerchantId
    })

    return { googlePayPaymentsClient, googlePayInstance }
  }

  const triggerGooglePay = async (...args: GooglePayArgs) => {
    const [gpayInstance, googlePayPaymentsClient, amount, billingAddress, submitOrderProcess, isTopUp] = args
    const paymentDataRequest = await gpayInstance.createPaymentDataRequest({
      transactionInfo: {
        currencyCode: currency,
        totalPriceStatus: 'FINAL',
        totalPrice: isTopUp ? orderStore.topupAmount.toFixed(2) : amount.toFixed(2)
      }
    });
    const cardPaymentMethod = (await paymentDataRequest).allowedPaymentMethods[0];
    cardPaymentMethod.parameters.billingAddressRequired = true;
    cardPaymentMethod.parameters.billingAddressParameters = {
      format: 'FULL'
    }
    googlePayPaymentsClient.loadPaymentData(paymentDataRequest).then(async function (paymentData) {
      await gpayInstance.parseResponse(paymentData, async function (err, result) {
        if (err) {
          throw new Error(err.name)
        }

        try {
          if (result.nonce && billingAddress) {
            await submitOrderProcess(result.nonce, CARD_TYPES.GOOGLE_PAY, billingAddress, PAYMENT_METHOD_IDS.GOOGLE_PAY, billingAddress.email ?? '');
          }
        } catch (error) {
          throw new Error(error)
        }
      })
    })
  }

  const createBraintreeGooglePay = async (data: PaymentMethodSetupData) => {
    // Render once
    if (!hasGooglePayButtonRendered) {
      setHasGooglePayButtonRendered(false)
      try {
        const { googlePayPaymentsClient, googlePayInstance } = await createBraintreeGooglePayInstance()
        const { amount, billingAddress, setErrorMessage, submitOrderProcess, isTopUp } = data
        const gpayInstance = (await googlePayInstance)
        
        if (!googlePayInstance) {
          throw Error('Error connecting to braintree')
        }

        googlePayPaymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: (await gpayInstance.createPaymentDataRequest()).allowedPaymentMethods,
        }).then(async function (isReadyToPay) {
          if (isReadyToPay.result) {
            const button = await googlePayPaymentsClient.createButton({
              buttonType: 'pay',
              buttonColor: 'black',
              buttonSizeMode: 'fill',
              onClick: async () => {
                try {
                  await triggerGooglePay(gpayInstance, googlePayPaymentsClient, amount, billingAddress, submitOrderProcess, isTopUp)
                } catch (error) {
                  console.error(error)
                  setErrorMessage(error)
                }
              }
            })

            const button2 = await googlePayPaymentsClient.createButton({
              buttonType: 'pay',
              buttonColor: 'black',
              buttonSizeMode: 'fill',
              onClick: async () => {
                try {
                  await triggerGooglePay(gpayInstance, googlePayPaymentsClient, amount, billingAddress, submitOrderProcess, isTopUp)
                } catch (error) {
                  console.error(error)
                  setErrorMessage(error)
                }
              }
            })
            
            const container1 = document.getElementById('GooglePay-container-1')

            if (container1) {
              // Clear existing buttons if switching between payments until refactored
              container1.innerHTML = ''
              container1.appendChild(button);
            }
            const container2 = document.getElementById('GooglePay-container-2')
            if (container2) {
              // Clear existing buttons if switching between payments until refactored
              container2.innerHTML = ''
              container2.appendChild(button2);
            }
            setHasGooglePayButtonRendered(true)
          }
        })
      } catch (error) {
        console.error(error)
      }
    }
  }

  return {
    initGooglePayButtons,
    hasGooglePayButtonRendered
  }
}
